import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useUserStore } from './user'
import { useRouter, type RouteLocationRaw } from 'vue-router'

const CHECK_PATH = '/api/auth/check'
const AVAILABLE_LOGIN_METHODS_PATH = '/api/auth/available_login_methods'
const AUTHENTICATE_PATH = '/api/auth/authenticate'
const RESET_PASSWORD_PATH = '/api/auth/reset_password'
const UNLOCK_ACCOUNT_PATH = '/api/auth/unlock_account'
const RESEND_CONFIRMATION_PATH = '/api/auth/resend_confirmation'

type ErrorResponse = {
  error: string,
  message?: string,
  tfa_token?: string,
}

type TokenResponse = {
  email: string,
  token: string,
}

type CheckResponse = {
  email: string,
  token?: string,
}

type AuthResponse = TokenResponse | ErrorResponse

type CheckTokenResponse = CheckResponse | ErrorResponse

export type AuthMethod = 'password' | 'webauthn'

export type AuthMethodResponse = {
  available_methods: AuthMethod[]
}


export class AuthError extends Error {
  declare cause: ErrorResponse
  constructor(message: string, cause: ErrorResponse) {
    super(message)
    this.cause = cause
  }
}

export class OTPRequestError extends Error {
  declare cause: ErrorResponse
  declare tfa_token: string
  constructor(message: string, cause: ErrorResponse, tfa_token: string) {
    super(message)
    this.cause = cause
    this.tfa_token = tfa_token
  }
}

export const useAuthStore = defineStore('auth', () => {
  const userStore = useUserStore()

  const authenticated = ref(false)
  const locked = ref(false)
  const availableMethods = ref<AuthMethod[]>([])
  const authMethod = ref<AuthMethod | null>(null)
  const email = ref<string | null>(null)
  const token = ref<string | null>(null)

  const authMethodComputed = computed(() => authMethod.value)
  const availableMethodsComputed = computed(() => availableMethods.value)

  const router = useRouter()

  function url(path: string) {
    return `${import.meta.env.VITE_BACKEND_HOST}${path}`
  }

  let promise: Promise<void>
  const init = () => {
    return promise ||= initialize()
  }

  async function initialize() {
    const savedToken = localStorage.getItem('token')
    const savedEmail = localStorage.getItem('user-email')
    const savedLocked = localStorage.getItem('locked')
    authenticated.value = false
    if(savedToken) {
      await checkToken(savedToken)
    }
    if (!authenticated.value && savedEmail) {
      await checkLoginMethod(savedEmail)
    }
    if(savedLocked) {
      locked.value = savedLocked === 'true'
    }
  }

  async function storeLocation(location: RouteLocationRaw) {
    sessionStorage.setItem('location', JSON.stringify(location))
  }

  async function checkLoginMethod(value: string): Promise<boolean> {
    const query = await fetch(url(AVAILABLE_LOGIN_METHODS_PATH), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ email: value }),
    })
    const response = await query.json() as AuthMethodResponse
    email.value = value
    availableMethods.value = response.available_methods
    authMethod.value = response.available_methods[0]
    await userStore.init({id: 0, email: value})
    return true
  }

  async function checkToken(token: string) {
    const query = await fetch(url(CHECK_PATH), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: `Bearer ${token}`,
      },
    })
    const response = await query.json() as CheckTokenResponse
    if(query.status < 400) {
      setAuthData({token, ...response} as CheckResponse)
      await userStore.loadUser()
      authenticated.value = true
    } else {
      const error = response as ErrorResponse
      localStorage.removeItem('token')
      userStore.clearUser()
    }
  }

  async function loginWithPassword(value: string, password: string) {
    const query = await fetch(url(AUTHENTICATE_PATH), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ email: value, password }),
    })
    const response = await query.json() as AuthResponse
    if(query.status >= 400) {
      const error = response as ErrorResponse
      if(error.error === 'locked') {
        setLocked(true)
      }
      if(error.error === 'otp_required' && error.tfa_token) {
        throw new OTPRequestError(error.error, error, error.tfa_token)
      }
      throw new AuthError(error.error, error)
    } else {
      setAuthData(response as TokenResponse)
      await userStore.loadUser()
      authenticated.value = true
      setLocked(false)
      redirect()
    }
  }

  async function loginWithOTP(tfa_token: string, otp_attempt: string) {
    const query = await fetch(url(AUTHENTICATE_PATH), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ tfa_token, otp_attempt }),
    })
    const response = await query.json() as AuthResponse
    if(query.status >= 400) {
      const error = response as ErrorResponse
      if((error.error === 'otp_required' || error.error === 'invalid_otp') && error.tfa_token) {
        throw new OTPRequestError(error.error, error, error.tfa_token)
      }
      throw new AuthError(error.error, error)
    } else {
      setAuthData(response as TokenResponse)
      await userStore.loadUser()
      authenticated.value = true
      setLocked(false)
      redirect()
    }
  }

  async function loginWithQRCode(qrcode_token: string) {
    const query = await fetch(url(AUTHENTICATE_PATH), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ qrcode_token }),
    })
    const response = await query.json() as AuthResponse
    if(query.status >= 400) {
      const error = response as ErrorResponse
      console.error(token)
      throw new AuthError(error.error, error)
    } else {
      setAuthData(response as TokenResponse)
      await userStore.loadUser()
      authenticated.value = true
      setLocked(false)
      redirect()
    }
  }

  async function loginWithWebAuthn(email: string) {
    /*
    // Richiedi challenge al server
    const challengeResponse = await axios.post('/api/auth/webauthn/challenge', {
      email,
    })

    // Converti challenge in ArrayBuffer per l'API WebAuthn
    const publicKey = {
      challenge: base64ToArrayBuffer(challengeResponse.data.challenge),
      rpId: window.location.hostname,
      allowCredentials: challengeResponse.data.allowCredentials.map(cred => ({
        type: 'public-key',
        id: base64ToArrayBuffer(cred.id),
      })),
      userVerification: 'preferred',
    }

    // Richiedi autenticazione al browser
    const assertion = await navigator.credentials.get({
      publicKey,
    })

    // Invia risposta al server
    const response = await axios.post('/api/auth/webauthn/login', {
      email,
      assertionResponse: {
        id: assertion.id,
        rawId: arrayBufferToBase64(assertion.rawId),
        response: {
          authenticatorData: arrayBufferToBase64(assertion.response.authenticatorData),
          clientDataJSON: arrayBufferToBase64(assertion.response.clientDataJSON),
          signature: arrayBufferToBase64(assertion.response.signature),
        },
      },
    })

    this.setAuthData(response.data)
  */
  }

  async function requestUnlock(inputEmail: string | null = null) {
    try{
      const query = await fetch(url(UNLOCK_ACCOUNT_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ email: inputEmail || email.value }),
      })
      const response = await query.json() as ErrorResponse
      if(query.status >= 400) {
        const error = response as ErrorResponse
        console.warn('requestUnlock', 'error %o', error)
        return false
      } else {
        return true
      }
    } catch (error) {
      console.error('requestUnlock', 'error %o', error)
      return false
    }
  }

  async function requestVerify() {
    try{
      const query = await fetch(url(RESEND_CONFIRMATION_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ email: email.value }),
      })
      const response = await query.json() as ErrorResponse
      if(query.status >= 400) {
        const error = response as ErrorResponse
        console.warn('requestVerify', 'error %o', error)
        return false
      } else {
        return true
      }
    } catch (error) {
      console.error('requestVerify', 'error %o', error)
      return false
    }
  }

  async function requestChangePassword(email: string) {
    try{
      const query = await fetch(url(RESET_PASSWORD_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ email }),
      })
      const response = await query.json() as ErrorResponse
      if(query.status >= 400) {
        const error = response as ErrorResponse
        console.warn('requestChangePassword', 'error %o', error)
        return false
      } else {
        return true
      }
    } catch (error) {
      console.error('requestChangePassword', 'error %o', error)
      return false
    }
  }

  async function logout() {
    localStorage.removeItem('token')
    localStorage.removeItem('locked')
    authenticated.value = false
    locked.value = false
    window.location.reload()
  }

  function setAuthData(data: TokenResponse | CheckResponse) {
    email.value = data.email
    localStorage.setItem('user-email', data.email)
    if('token' in data && data.token) {
      token.value = data.token
      localStorage.setItem('token', data.token)
    }
  }

  function redirect() {
    setTimeout(() => {
      const serializedLocation = sessionStorage.getItem('location')
      if(serializedLocation) {
        sessionStorage.removeItem('location')
        const location = JSON.parse(serializedLocation)
        router.push(location)
      } else {
        router.push('/')
      }
    }, 2000)
  }

  function setLocked(value: boolean) {
    localStorage.setItem('locked', value.toString())
    locked.value = value
  }

  return {
    authMethod: authMethodComputed,
    availableMethods: availableMethodsComputed,
    authenticated,
    locked,
    email,
    token,
    init,
    storeLocation,
    checkLoginMethod,
    loginWithPassword,
    loginWithOTP,
    loginWithQRCode,
    loginWithWebAuthn,
    requestUnlock,
    requestVerify,
    requestChangePassword,
    logout,
  }
})

// Utility functions for WebAuthn
function arrayBufferToBase64(buffer: ArrayBuffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
}

function base64ToArrayBuffer(base64: string) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}
